import { DateTime } from "luxon";
import { useTranslation } from "next-i18next";
import { useMemo } from "react";

import { CheckoutInstallmentStatusBanner } from "@filadd/phi-plus-layout";
import { cx, css } from "@filadd/phi-plus-styled-system/css";

import { useCourseSlug } from "hooks/course";
import { useLayoutTrinket } from "hooks/layout";

import RichTextPreview from "components/commons/RichTextPreview";

import { InstallmentStatus } from "types/installment";
import { TrinketNames } from "types/layout";

const INSTALLMENT_NOTIFICATION_STATUS = {
  SOON_DUE: "soonDue",
  OVERDUE: "overdue",
} as const;

interface InstallmentNotificationBannerProps {
  className?: string;
}
export const InstallmentNotificationBanner = ({
  className,
}: InstallmentNotificationBannerProps) => {
  const { t } = useTranslation("common");

  const courseSlug = useCourseSlug();
  const { data: trinket } = useLayoutTrinket({
    trinketName: TrinketNames.INSTALLMENTS,
    courseSlug,
  });

  const nextInstallment = useMemo(() => {
    if (
      !trinket ||
      trinket.name !== TrinketNames.INSTALLMENTS ||
      !trinket.data?.nextInstallment
    ) {
      return null;
    }

    const nextUnpaidInstallment = trinket.data.nextInstallment;

    if (nextUnpaidInstallment.status === InstallmentStatus.OVERDUE) {
      return {
        status: INSTALLMENT_NOTIFICATION_STATUS.OVERDUE,
        dueDate: DateTime.fromISO(nextUnpaidInstallment.dueDate),
      };
    }

    const daysUntilDue = DateTime.fromISO(nextUnpaidInstallment.dueDate)
      .diff(DateTime.now(), "days")
      .as("days");

    return daysUntilDue < 6
      ? {
          status: INSTALLMENT_NOTIFICATION_STATUS.SOON_DUE,
          dueDate: DateTime.fromISO(nextUnpaidInstallment.dueDate),
        }
      : null;
  }, [trinket]);

  return nextInstallment ? (
    <div className={cx(css({ p: 2, mb: 4, width: "full" }), className)}>
      <CheckoutInstallmentStatusBanner
        status={nextInstallment.status}
        description={
          <div
            className={css({
              "&&& *": {
                p: 0,
                fontSize: "inherit",
              },
            })}
          >
            <RichTextPreview
              content={t(`installmentBanner.${nextInstallment.status}`, {
                dueDate: nextInstallment.dueDate.toFormat("dd/LL"),
                link: `/checkout/installments/${courseSlug}`,
              })}
            />
          </div>
        }
      />
    </div>
  ) : null;
};
